import { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg } from "../lib/api";
import { fmtNum, fmtDate } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Field, Btn, DataTable, inputCls } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Plus, ArrowLeftRight, AlertTriangle, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Inventory() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("stock");
  const [matrix, setMatrix] = useState(null);
  const [mutations, setMutations] = useState([]);
  const [showItem, setShowItem] = useState(false);
  const [showWh, setShowWh] = useState(false);
  const [showTrx, setShowTrx] = useState(null);
  const [itemForm, setItemForm] = useState({ code: "", name: "", unit: "pcs", min_stock: 0 });
  const [whForm, setWhForm] = useState({ name: "", location: "" });
  const [trxForm, setTrxForm] = useState({ item_id: "", warehouse_id: "", to_warehouse_id: "", qty: 0, note: "" });
  const [showBulk, setShowBulk] = useState(false);
  const bulkFileRef = useRef(null);
  const [editItemId, setEditItemId] = useState(null);

  const openEditItem = (it) => {
    setEditItemId(it.id);
    setItemForm({ code: it.code, name: it.name, unit: it.unit, min_stock: it.min_stock });
    setShowItem(true);
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`Hapus barang "${it.name}" (${it.code})? Riwayat mutasi lama tetap tersimpan.`)) return;
    try { await api.delete(`/inventory/items/${it.id}`); toast.success("Barang dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doBulk = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/inventory/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import selesai: ${r.data.created} barang baru, ${r.data.updated} diperbarui, ${r.data.stock_entries} stok awal masuk`);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} baris gagal: ${r.data.errors[0]}`);
      setShowBulk(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const load = useCallback(() => {
    api.get("/inventory/stock").then((r) => setMatrix(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/inventory/mutations").then((r) => setMutations(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const wrap = (fn, okMsg, closer) => async () => {
    try {
      const res = await fn();
      if (res?.data?.status === "pending_approval") toast.info(res.data.message);
      else toast.success(okMsg);
      closer && closer();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const lowRows = matrix?.rows.filter((r) => r.low_stock) || [];

  return (
    <div data-testid="inventory-page">
      <PageHeader title="Inventory" subtitle="Multi-gudang, mutasi stok (masuk/keluar/transfer), peringatan stok minimum" testid="inventory-header">
        {hasPerm("inventory", "create") && (
          <>
            <Btn tone="ghost" onClick={() => setShowWh(true)} data-testid="add-warehouse-button"><Plus className="w-4 h-4" /> Gudang</Btn>
            <Btn tone="ghost" onClick={() => { setEditItemId(null); setItemForm({ code: "", name: "", unit: "pcs", min_stock: 0 }); setShowItem(true); }} data-testid="add-item-button"><Plus className="w-4 h-4" /> Barang</Btn>
            <Btn tone="amber" onClick={() => setShowTrx("adjust")} data-testid="adjust-stock-button">Penyesuaian</Btn>
            <Btn onClick={() => setShowTrx("transfer")} data-testid="transfer-stock-button"><ArrowLeftRight className="w-4 h-4" /> Transfer</Btn>
            <Btn tone="ghost" onClick={() => setShowBulk(true)} data-testid="bulk-upload-button"><Upload className="w-4 h-4" /> Bulk Upload Excel</Btn>
          </>
        )}
      </PageHeader>

      {lowRows.length > 0 && (
        <div className="mb-5 bg-rose-500/10 border border-rose-500/40 rounded-xl p-4" data-testid="low-stock-alert">
          <div className="flex items-center gap-2 text-rose-300 text-sm font-medium mb-2"><AlertTriangle className="w-4 h-4" /> Peringatan Stok Minimum</div>
          <div className="flex gap-2 flex-wrap">
            {lowRows.map((r) => <Badge key={r.item.id} tone="rose">{r.item.name}: {fmtNum(r.total)} {r.item.unit} (min {fmtNum(r.item.min_stock)})</Badge>)}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-5 flex-wrap">
        {[["stock", "Matriks Stok"], ["mutations", "Riwayat Mutasi"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`inv-tab-${k}`}
            className={`text-sm px-4 py-2 rounded-lg border transition-colors ${tab === k ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "stock" && matrix && (
        <div className="overflow-x-auto rounded-xl border border-slate-800" data-testid="stock-matrix">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 border-b border-slate-800">
                <th className="text-left text-xs font-mono uppercase tracking-wider text-slate-500 px-4 py-3">Barang</th>
                {matrix.warehouses.map((w) => <th key={w.id} className="text-right text-xs font-mono uppercase tracking-wider text-slate-500 px-4 py-3">{w.name}</th>)}
                <th className="text-right text-xs font-mono uppercase tracking-wider text-slate-500 px-4 py-3">Total</th>
                <th className="text-right text-xs font-mono uppercase tracking-wider text-slate-500 px-4 py-3">Min</th>
                <th className="text-xs font-mono uppercase tracking-wider text-slate-500 px-4 py-3">Status</th>
                {(hasPerm("inventory", "edit") || hasPerm("inventory", "delete")) && <th className="text-right text-xs font-mono uppercase tracking-wider text-slate-500 px-4 py-3">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((r) => (
                <tr key={r.item.id} className="border-b border-slate-800/60 hover:bg-slate-900/70" data-testid={`stock-row-${r.item.code}`}>
                  <td className="px-4 py-3"><span className="font-mono text-xs text-slate-500 mr-2">{r.item.code}</span><span className="text-slate-200">{r.item.name}</span></td>
                  {matrix.warehouses.map((w) => <td key={w.id} className="px-4 py-3 text-right font-mono text-xs text-slate-300">{fmtNum(r.per_warehouse[w.id] || 0)}</td>)}
                  <td className="px-4 py-3 text-right font-mono text-xs text-emerald-400">{fmtNum(r.total)} {r.item.unit}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-slate-500">{fmtNum(r.item.min_stock)}</td>
                  <td className="px-4 py-3 text-center">{r.low_stock ? <Badge tone="rose">Kritis</Badge> : <Badge tone="emerald">Aman</Badge>}</td>
                  {(hasPerm("inventory", "edit") || hasPerm("inventory", "delete")) && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {hasPerm("inventory", "edit") && <button className="text-amber-400 hover:text-amber-300 text-xs font-medium mr-3 transition-colors" onClick={() => openEditItem(r.item)} data-testid={`item-edit-${r.item.code}`}>Edit</button>}
                      {hasPerm("inventory", "delete") && <button className="text-rose-400 hover:text-rose-300 text-xs font-medium transition-colors" onClick={() => deleteItem(r.item)} data-testid={`item-delete-${r.item.code}`}>Hapus</button>}
                    </td>
                  )}
                </tr>
              ))}
              {matrix.rows.length === 0 && (
                <tr>
                  <td colSpan={matrix.warehouses.length + ((hasPerm("inventory", "edit") || hasPerm("inventory", "delete")) ? 5 : 4)} className="text-center text-slate-500 py-10" data-testid="stock-empty-state">
                    Belum ada data barang. Tambahkan via tombol <span className="text-slate-300 font-medium">+ Barang</span> atau <span className="text-slate-300 font-medium">Bulk Upload Excel</span> (kolom wajib: <code className="font-mono text-emerald-400">code, name</code>).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "mutations" && (
        <DataTable testid="mutations-table" searchKeys={["item_name", "ref", "warehouse_name"]}
          columns={[
            { header: "Referensi", accessor: "ref", mono: true },
            { header: "Tanggal", render: (r) => fmtDate(r.date) },
            { header: "Barang", accessor: "item_name" },
            { header: "Gudang", accessor: "warehouse_name" },
            { header: "Tipe", render: (r) => <Badge tone={{ in: "emerald", out: "rose" }[r.type] || "slate"}>{r.type === "in" ? "MASUK" : "KELUAR"}</Badge> },
            { header: "Qty", render: (r) => <span className="font-mono text-xs">{fmtNum(r.qty)}</span> },
            { header: "Keterangan", render: (r) => <span className="text-xs text-slate-500">{r.note}</span> },
          ]}
          rows={mutations} empty="Belum ada mutasi" />
      )}

      <Modal open={showBulk} onClose={() => setShowBulk(false)} title="Bulk Upload Material (.xlsx / .csv)" testid="bulk-upload-modal">
        <div className="space-y-4">
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-xs text-slate-400 space-y-1">
            <div className="text-slate-300 font-medium mb-1">Format kolom (baris pertama = header):</div>
            <div><code className="text-emerald-400 font-mono">code, name, unit, min_stock, warehouse, stock_awal, price</code></div>
            <div>&bull; <b>code</b> &amp; <b>name</b> wajib. Code yang sudah ada akan diperbarui (harga & stok minimum).</div>
            <div>&bull; <b>stock_awal</b> &gt; 0 akan membuat mutasi MASUK ke gudang sesuai kolom <b>warehouse</b> (nama gudang; kosong = gudang pertama).</div>
            <div>&bull; <b>price</b> = harga supplier terakhir, dipakai sebagai estimasi biaya request material proyek.</div>
          </div>
          <input ref={bulkFileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={doBulk} data-testid="bulk-file-input" />
          <Btn className="w-full justify-center" onClick={() => bulkFileRef.current.click()} data-testid="bulk-select-file-button"><Upload className="w-4 h-4" /> Pilih File & Import</Btn>
        </div>
      </Modal>

      <Modal open={showItem} onClose={() => { setShowItem(false); setEditItemId(null); }} title={editItemId ? "Edit Barang" : "Tambah Barang"} testid="item-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Kode"><input className={inputCls} value={itemForm.code} onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })} data-testid="item-code-input" /></Field>
            <Field label="Satuan"><input className={inputCls} value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} data-testid="item-unit-input" /></Field>
          </div>
          <Field label="Nama Barang"><input className={inputCls} value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} data-testid="item-name-input" /></Field>
          <Field label="Stok Minimum"><input type="number" className={inputCls} value={itemForm.min_stock} onChange={(e) => setItemForm({ ...itemForm, min_stock: +e.target.value })} data-testid="item-minstock-input" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => { setShowItem(false); setEditItemId(null); }}>Batal</Btn>
            <Btn onClick={wrap(() => editItemId ? api.put(`/inventory/items/${editItemId}`, itemForm) : api.post("/inventory/items", itemForm), editItemId ? "Barang diperbarui" : "Barang ditambahkan", () => { setShowItem(false); setEditItemId(null); })} data-testid="item-save-button">Simpan</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={showWh} onClose={() => setShowWh(false)} title="Tambah Gudang" testid="warehouse-form-modal">
        <div className="space-y-3">
          <Field label="Nama Gudang"><input className={inputCls} value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} data-testid="wh-name-input" /></Field>
          <Field label="Lokasi"><input className={inputCls} value={whForm.location} onChange={(e) => setWhForm({ ...whForm, location: e.target.value })} data-testid="wh-location-input" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowWh(false)}>Batal</Btn>
            <Btn onClick={wrap(() => api.post("/inventory/warehouses", whForm), "Gudang ditambahkan", () => setShowWh(false))} data-testid="wh-save-button">Simpan</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!showTrx} onClose={() => setShowTrx(null)} title={showTrx === "transfer" ? "Transfer Antar Gudang" : "Penyesuaian Stok"} testid="trx-form-modal">
        {matrix && (
          <div className="space-y-3">
            <Field label="Barang">
              <select className={inputCls} value={trxForm.item_id} onChange={(e) => setTrxForm({ ...trxForm, item_id: e.target.value })} data-testid="trx-item-select">
                <option value="">Pilih barang...</option>
                {matrix.rows.map((r) => <option key={r.item.id} value={r.item.id}>{r.item.code} - {r.item.name}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={showTrx === "transfer" ? "Dari Gudang" : "Gudang"}>
                <select className={inputCls} value={trxForm.warehouse_id} onChange={(e) => setTrxForm({ ...trxForm, warehouse_id: e.target.value })} data-testid="trx-from-select">
                  <option value="">Pilih...</option>
                  {matrix.warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </Field>
              {showTrx === "transfer" ? (
                <Field label="Ke Gudang">
                  <select className={inputCls} value={trxForm.to_warehouse_id} onChange={(e) => setTrxForm({ ...trxForm, to_warehouse_id: e.target.value })} data-testid="trx-to-select">
                    <option value="">Pilih...</option>
                    {matrix.warehouses.filter((w) => w.id !== trxForm.warehouse_id).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </Field>
              ) : (
                <Field label="Tipe">
                  <select className={inputCls} value={trxForm.type || "in"} onChange={(e) => setTrxForm({ ...trxForm, type: e.target.value })} data-testid="trx-type-select">
                    <option value="in">Masuk</option><option value="out">Keluar</option>
                  </select>
                </Field>
              )}
            </div>
            <Field label="Qty"><input type="number" className={inputCls} value={trxForm.qty || ""} onChange={(e) => setTrxForm({ ...trxForm, qty: +e.target.value })} data-testid="trx-qty-input" /></Field>
            <Field label="Catatan"><input className={inputCls} value={trxForm.note} onChange={(e) => setTrxForm({ ...trxForm, note: e.target.value })} data-testid="trx-note-input" /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <Btn tone="ghost" onClick={() => setShowTrx(null)}>Batal</Btn>
              <Btn
                onClick={wrap(
                  () => showTrx === "transfer"
                    ? api.post("/inventory/transfer", { item_id: trxForm.item_id, from_warehouse_id: trxForm.warehouse_id, to_warehouse_id: trxForm.to_warehouse_id, qty: trxForm.qty, note: trxForm.note })
                    : api.post("/inventory/adjust", { item_id: trxForm.item_id, warehouse_id: trxForm.warehouse_id, type: trxForm.type || "in", qty: trxForm.qty, note: trxForm.note }),
                  "Mutasi stok tercatat", () => setShowTrx(null)
                )}
                data-testid="trx-save-button">Simpan</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
